// Gráfico de linhas em SVG, sem dependência externa.
// Cada série tem um rótulo, cor e pontos {x (rótulo), y (valor|null)}.

export interface ChartLine {
  label: string
  color: string
  data: Array<{ x: string; y: number | null }>
}

const W = 820
const H = 320
const M = { top: 16, right: 16, bottom: 40, left: 56 }

/** Ticks "redondos" contidos DENTRO de [min, max] — não estica o domínio. */
function niceTicks(min: number, max: number, count = 5): number[] {
  const span = max - min
  if (span <= 0) return [min]
  const step0 = span / count
  const mag = Math.pow(10, Math.floor(Math.log10(step0)))
  const norm = step0 / mag
  const step = (norm >= 7 ? 10 : norm >= 3 ? 5 : norm >= 1.5 ? 2 : 1) * mag
  const start = Math.ceil(min / step) * step // primeiro tick já dentro do domínio
  const ticks: number[] = []
  for (let v = start; v <= max + step / 1e6; v += step) ticks.push(Math.round(v * 1e6) / 1e6)
  return ticks
}

function fmt(n: number): string {
  if (Math.abs(n) >= 1000) return n.toLocaleString('pt-BR', { maximumFractionDigits: 0 })
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 2 })
}

export function LineChartSvg({ lines, xLabels }: { lines: ChartLine[]; xLabels: string[] }) {
  const innerW = W - M.left - M.right
  const innerH = H - M.top - M.bottom

  const values = lines.flatMap((l) => l.data.map((d) => d.y).filter((v): v is number => v != null))
  if (xLabels.length === 0 || values.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-slate-300 text-sm text-slate-400">
        Sem dados para os cortes/métrica selecionados ainda.
      </div>
    )
  }

  // Auto-zoom: escala colada nos valores, com folga em cima e embaixo para que
  // nenhum ponto encoste no topo ou na base — quanto menor a variação, mais perto
  // do mín/máx a escala fica. (Não ancora no zero.)
  const dataMin = Math.min(...values)
  const dataMax = Math.max(...values)
  const span = dataMax - dataMin
  const pad = span > 0 ? span * 0.08 : Math.abs(dataMax) * 0.1 || 1
  const yMin = dataMin - pad
  const yMax = dataMax + pad
  const ticks = niceTicks(yMin, yMax)

  const x = (i: number) =>
    M.left + (xLabels.length === 1 ? innerW / 2 : (i / (xLabels.length - 1)) * innerW)
  const y = (v: number) => M.top + innerH - ((v - yMin) / (yMax - yMin || 1)) * innerH

  // Mostra no máximo ~8 rótulos no eixo X.
  const stepX = Math.ceil(xLabels.length / 8)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img">
      {/* Grade + eixo Y */}
      {ticks.map((t) => (
        <g key={t}>
          <line x1={M.left} x2={W - M.right} y1={y(t)} y2={y(t)} stroke="#e2e8f0" strokeWidth={1} />
          <text x={M.left - 8} y={y(t) + 4} textAnchor="end" className="fill-slate-400 text-[10px]">
            {fmt(t)}
          </text>
        </g>
      ))}
      {/* Rótulos do eixo X */}
      {xLabels.map((lbl, i) =>
        i % stepX === 0 || i === xLabels.length - 1 ? (
          <text key={i} x={x(i)} y={H - 14} textAnchor="middle" className="fill-slate-400 text-[10px]">
            {lbl.slice(8, 10)}/{lbl.slice(5, 7)}
          </text>
        ) : null
      )}
      {/* Linhas */}
      {lines.map((line) => {
        const pts = line.data
          .map((d, i) => (d.y == null ? null : `${x(i)},${y(d.y)}`))
          .filter((p): p is string => p != null)
        return (
          <g key={line.label}>
            <polyline points={pts.join(' ')} fill="none" stroke={line.color} strokeWidth={2} />
            {line.data.map((d, i) =>
              d.y == null ? null : (
                <circle key={i} cx={x(i)} cy={y(d.y)} r={3} fill={line.color}>
                  <title>
                    {line.label} · {d.x}: {fmt(d.y)}
                  </title>
                </circle>
              )
            )}
          </g>
        )
      })}
    </svg>
  )
}
